import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import ReportingService from "Lib/API/reporting";
import { numberFormat } from "helper";
import useWidgetData from "./useWidgetData";
import WidgetWrapper from "./WidgetWrapper";
import "./widgets.scss";

const fmt = (n) => numberFormat({ number: n ?? 0, decimal: 0, style: "currency" });

const clickableRowStyle = { cursor: "pointer" };

const OutstandingWidget = ({ customizeMode, onRemove, settings, onConfigure, dateRange }) => {
  const filter = useMemo(() => ({
    ledger: settings?.ledger || [],
    subLedger: settings?.subLedger || [],
    dateRange: dateRange || null,
  }), [settings?.ledger, settings?.subLedger, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetcher = useCallback((token) => ReportingService.getWidgetOutstandingBalances(token, filter), [filter]);
  const { data, loading, error, retry } = useWidgetData(fetcher);
  const history = useHistory();

  const ledgerLabel = useMemo(() => {
    if (!settings?.ledgerMeta?.length) return null;
    const names = settings.ledgerMeta.map((m) => m.label);
    return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
  }, [settings?.ledgerMeta]); // eslint-disable-line react-hooks/exhaustive-deps

  const pct = data?.collectedPct ?? null;

  const buildUrl = (base) => {
    const params = new URLSearchParams(base);
    (filter.ledger || []).forEach((id) => params.append("ledger", id));
    (filter.subLedger || []).forEach((id) => params.append("subLedger", id));
    return `/finance?${params}`;
  };

  const goToOverdue = (days) => {
    if (customizeMode) return;
    history.push(buildUrl({ overdue: days }));
  };

  // If a custom label is set, use it as title and show ledger names as subtitle.
  // If only a ledger filter is set (no custom label), use ledger names as title.
  const title = settings?.label || ledgerLabel || "Balance";
  const subtitle = settings?.label ? ledgerLabel : null;

  return (
    <WidgetWrapper
      title={title}
      subtitle={subtitle}
      loading={loading}
      error={error}
      onRetry={retry}
      customizeMode={customizeMode}
      onRemove={onRemove}
      onConfigure={onConfigure}
    >
      <div className="stat-widget">
        <div style={{ display: "flex", gap: 16, marginBottom: 4 }}>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div className="stat-widget__value" style={{ color: "#ff2b6b", fontSize: "inherit" }}>{fmt(data?.total)}</div>
            <div className="stat-widget__sub">Total unpaid</div>
          </div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div className="stat-widget__value" style={{ color: "#7fdc90", fontSize: "inherit" }}>{fmt(data?.totalPaid)}</div>
            <div className="stat-widget__sub">Total paid</div>
          </div>
        </div>
        <div
          className="stat-widget__row stat-widget__row--clickable"
          style={clickableRowStyle}
          onClick={() => goToOverdue(30)}
        >
          <span className="stat-widget__label">Overdue (&gt;30 days)</span>
          <span className="stat-widget__val stat-widget__val--warn">{fmt(data?.overdue30)}</span>
        </div>
        <div
          className="stat-widget__row stat-widget__row--clickable"
          style={clickableRowStyle}
          onClick={() => goToOverdue(60)}
        >
          <span className="stat-widget__label">Overdue (&gt;60 days)</span>
          <span className="stat-widget__val stat-widget__val--red">{fmt(data?.overdue60)}</span>
        </div>
        <div
          className="stat-widget__row stat-widget__row--clickable"
          style={clickableRowStyle}
          onClick={() => { if (!customizeMode) history.push(buildUrl({ charge_status: "open" })); }}
        >
          <span className="stat-widget__label">Open invoices</span>
          <span className="stat-widget__val">{data?.invoiceCount ?? 0}</span>
        </div>
        {pct !== null && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#4B5568", marginBottom: 4 }}>
              <span>Collected vs. billed this period</span>
              <span>{pct}%</span>
            </div>
            <div className="prog-bar">
              <div className="prog-bar__fill prog-bar__fill--green" style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          </div>
        )}
      </div>
    </WidgetWrapper>
  );
};

export default OutstandingWidget;
